import React from 'react';
import { observer } from 'mobx-react';
import styled, { useTheme } from 'styled-components';
import get from 'lodash/get';

import { SmallText } from '@ui-library/typography';
import { Flex } from '@ui-library/flex';

import { useAccountManagementStore } from '../store';
import { useDesignVariant } from '../designVariantContext';

// Items wrap as whole units. `row-gap` spaces the wrapped lines; horizontal spacing comes
// from the separator's own margins (below), so no column-gap is set here.
const OptionList = styled(Flex)`
  flex-wrap: wrap;
  row-gap: 4px;
`;

// The separator is the item's own box, not part of its text. Putting a literal ' • ' in
// the text node lets the browser break the line *before* the bullet, stranding it at the
// start of the next line — which is what the Current row does, since its badge and ⋯ menu
// leave the summary too narrow to fit on one line. As an ::after it can only ever sit on
// the same line as the value it follows, and `nowrap` keeps each "Label: value" atomic so
// an item that does not fit moves down whole.
const OptionItem = styled(SmallText)`
  white-space: nowrap;

  &:not(:last-child)::after {
    content: '•';
    margin: 0 4px;
  }
`;

interface AccountOptionsProps {
  accountId: string;
}

/**
 * A one-line summary of an account's non-secret `options` fields, taken from its
 * account definition's form. Rendered only where the option detail identifies the
 * account (data-retention: provider/bucket/prefix/SSL) — see `accountDetails`.
 */
export const AccountOptions: React.FC<AccountOptionsProps> = observer(({ accountId }) => {
  // Every hook runs before the early returns below. This component bails out on data
  // that arrives asynchronously (accounts and definitions land in separate renders),
  // so a hook placed after a return would run on some renders of an instance and not
  // others. These three are all useContext, which React exempts from the hook list, so
  // today that only trips React's order-of-Hooks warning — but the first useState or
  // useMemo added below a return would make it throw. Rules-of-Hooks lint is off in
  // this repo (.eslintrc.json extends airbnb, not airbnb/hooks), so nothing else
  // catches it.
  const { findAccountById, compatibleAccountDefinitions } = useAccountManagementStore();
  const isNext = useDesignVariant() === 'next';
  const theme = useTheme();

  const accountDetails = findAccountById(accountId);
  // The account's definition may be absent from the compatible set (definitions
  // not loaded yet, or the account references a renamed/removed definition), so
  // this lookup is treated as optional rather than asserted.
  const accountDefinition = compatibleAccountDefinitions.find(
    (accountDef) => accountDef.name === accountDetails?.accountDefinitionName,
  );

  if (!accountDetails || !accountDefinition) {
    return null;
  }

  const viewOptions = accountDefinition.uiConfig.form.fields.filter(
    (field) => field.key.includes('options') && !('secret' in field && field.secret),
  );

  if (viewOptions.length === 0) {
    return null;
  }

  return (
    <OptionList flexDirection="row" className="m-t-sm">
      {viewOptions.map((option) => {
        const value = get(accountDetails, option.key);
        // Fall back to "Not set" for unset/empty options (e.g. an unset prefix)
        // instead of rendering a literal "undefined".
        const displayValue =
          value === undefined || value === null || value === '' ? 'Not set' : value;

        return (
          <OptionItem
            key={option.label}
            color={isNext ? theme.token.next.palette.grey500 : theme.token.colorTextTertiary}
          >
            {`${option.label}: ${displayValue}`}
          </OptionItem>
        );
      })}
    </OptionList>
  );
});
